import React, { useEffect, useState } from 'react';
import { Users, Mail, Phone, User, AlertCircle, Bell, Calendar, Instagram, Leaf, Clock3, ArrowRight, PartyPopper, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:8000'
    : 'https://crapule-bxl-production.up.railway.app'
);
const API = `${BACKEND_URL}/api`;

const modalShellClassName = 'border border-white/10 bg-[radial-gradient(circle_at_top,rgba(251,191,36,0.12),transparent_34%),linear-gradient(145deg,rgba(15,23,42,0.98),rgba(2,6,23,0.95))] p-0 text-white shadow-[0_24px_80px_rgba(0,0,0,0.55)] backdrop-blur-2xl';
const panelClassName = 'rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] sm:p-6';
const iconInputClassName = 'h-12 rounded-xl border-white/10 bg-white/[0.05] pl-10 text-white placeholder:text-slate-500 focus-visible:border-white/20 focus-visible:ring-1 focus-visible:ring-white/30';
const plainInputClassName = 'h-12 rounded-xl border-white/10 bg-white/[0.05] text-white placeholder:text-slate-500 focus-visible:border-white/20 focus-visible:ring-1 focus-visible:ring-white/30';
const selectTriggerClassName = 'h-12 rounded-xl border-white/10 bg-white/[0.05] text-white focus:ring-1 focus:ring-white/30';
const selectContentClassName = 'border-white/10 bg-slate-950/95 text-white';

const InfoChip = ({ icon: Icon, children, accent = false }) => (
  <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${accent ? 'border-amber-400/30 bg-amber-400/10 text-amber-100' : 'border-white/10 bg-white/[0.05] text-slate-200'}`}>
    <Icon size={14} className={accent ? 'text-amber-300' : 'text-slate-400'} />
    <span>{children}</span>
  </div>
);

const SectionCard = ({ eyebrow, title, description, children, className = '' }) => (
  <section className={`${panelClassName} ${className}`.trim()}>
    {(eyebrow || title || description) && (
      <div className="mb-4 space-y-1">
        {eyebrow && <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">{eyebrow}</p>}
        {title && <h3 className="text-lg font-semibold text-white">{title}</h3>}
        {description && <p className="text-sm leading-6 text-slate-400">{description}</p>}
      </div>
    )}
    {children}
  </section>
);

const EventHero = ({ eventInfo, reservationsOpen, hasPrice }) => {
  const volume = eventInfo?.currentVolume || {};
  const title = `Vol.${volume.number || '?'}`;
  const subtitle = volume.theme || 'Expérience culinaire';

  return (
    <div className="relative overflow-hidden rounded-[1.5rem] border border-white/10 bg-white/[0.04] p-4 shadow-[0_18px_50px_rgba(0,0,0,0.28)] sm:rounded-[1.75rem] sm:p-6">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent via-white/40" />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-slate-300">
              <Sparkles size={14} className="text-amber-300" />
              {reservationsOpen ? 'Réservations ouvertes' : 'Liste d’attente'}
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.28em] text-slate-500">{title}</p>
              <h2 className="text-2xl font-black tracking-tight text-white sm:text-[2rem]">{subtitle}</h2>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left sm:min-w-[170px] sm:text-right">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500">Tarif</p>
            <p className="mt-1 text-lg font-semibold text-white">{hasPrice ? `${volume.price}€ / pers.` : 'Sur sélection'}</p>
            <p className="mt-1 text-xs text-slate-400">
              {reservationsOpen ? 'Validation finale par l’équipe' : 'Ouverture annoncée par email'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <InfoChip icon={Calendar} accent>{volume.date || 'Date à venir'}</InfoChip>
          {volume.time && <InfoChip icon={Clock3}>{volume.time}</InfoChip>}
          <InfoChip icon={Bell}>{reservationsOpen ? 'Réponse par email' : 'Alerte d’ouverture'}</InfoChip>
        </div>
        <p className="max-w-2xl text-sm leading-6 text-slate-300">
          {reservationsOpen
            ? 'Complétez votre demande en quelques étapes. Une confirmation vous est ensuite envoyée par l’équipe.'
            : 'Les réservations ne sont pas encore ouvertes. Laissez votre email pour recevoir l’ouverture dès qu’elle est annoncée.'}
        </p>
      </div>
    </div>
  );
};

const Reservation = ({ isOpen, onClose, eventInfo }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: '2',
    menuChoice: 'meat',
  });
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuSelections, setMenuSelections] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [reservationDetails, setReservationDetails] = useState(null);
  const [calendarConfig, setCalendarConfig] = useState({ enabled: false, eventEnabled: false, slotDurationMinutes: 30 });
  const [calendarSlots, setCalendarSlots] = useState({ month: '', days: {} });
  const [reservationType, setReservationType] = useState('event');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');

  // Check if reservations are open
  const reservationsOpen = eventInfo.currentVolume.reservationsOpen || eventInfo.currentVolume.status === 'available';
  const hasPrice = eventInfo.currentVolume.price !== null && eventInfo.currentVolume.price !== undefined;

  useEffect(() => {
    const loadMenu = async () => {
      try {
        const eventsRes = await axios.get(`${API}/events`);
        const events = eventsRes.data.events || [];
        const ev = events.find(e => e.volume === eventInfo.currentVolume.number);
        if (!ev) {
          setMenuOptions([]);
          setMenuSelections([]);
          return;
        }
        const menuRes = await axios.get(`${API}/menus/${ev.id}`);
        const dishes = menuRes.data.menu?.dishes || [];
        setMenuOptions(dishes);
        // reset selections if dishes changed
        setMenuSelections(prev => {
          const names = new Set(dishes.map(d => d.name));
          return prev.filter(s => names.has(s.name));
        });
      } catch {
        setMenuOptions([]);
        setMenuSelections([]);
      }
    };
    const loadCalendar = async () => {
      try {
        const cfgRes = await axios.get(`${API}/calendar/public-config`).catch(() => null);
        if (cfgRes && cfgRes.data) {
          setCalendarConfig(cfgRes.data);
          const now = new Date();
          const y = now.getFullYear();
          const m = now.getMonth() + 1;
          const slotsRes = await axios.get(`${API}/calendar/slots`, { params: { year: y, month: m } }).catch(() => null);
          if (slotsRes && slotsRes.data) {
            setCalendarSlots(slotsRes.data);
          }
        }
      } catch {
        setCalendarConfig({ enabled: false, eventEnabled: false, slotDurationMinutes: 30 });
        setCalendarSlots({ month: '', days: {} });
      }
    };
    if (isOpen) {
      loadMenu();
      loadCalendar();
    }
  }, [isOpen, eventInfo.currentVolume.number]);

  const setSelectionQty = (name, qty) => {
    setMenuSelections(prev => {
      const q = Math.max(0, parseInt(qty || '0'));
      const existing = prev.find(s => s.name === name);
      if (existing) {
        return prev.map(s => s.name === name ? { name, quantity: q } : s);
      }
      return [...prev, { name, quantity: q }];
    });
  };

  const computeMenuTotal = () => {
    if (!menuOptions.length || !menuSelections.length) return null;
    const priceMap = Object.fromEntries(menuOptions.map(d => [d.name, parseInt(d.price || 0)]));
    return menuSelections.reduce((sum, s) => sum + (priceMap[s.name] || 0) * (parseInt(s.quantity || 0)), 0);
  };
  const typeLabel = (t) => (t === 'starter' ? 'Entrée' : t === 'main' ? 'Plat' : t === 'dessert' ? 'Dessert' : t);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        guests: parseInt(formData.guests),
        eventVolume: eventInfo.currentVolume.number
      };
      if (menuOptions.length) {
        payload.menuSelections = menuSelections.filter(s => s.quantity > 0);
      }
      if (calendarConfig.enabled) {
        payload.reservationType = reservationType;
        if (reservationType === 'regular') {
          payload.reservationDate = selectedDate || null;
          payload.reservationTime = selectedTime || null;
        } else if (calendarConfig.eventEnabled) {
          payload.reservationType = 'event';
          // Try to normalize event date to YYYY-MM-DD for day-level aggregation
          try {
            const parts = String(eventInfo.currentVolume.date || '').split(/[-/]/);
            let yy = 0, mm = 0, dd = 0;
            if (parts.length === 3) {
              dd = parseInt(parts[0], 10); mm = parseInt(parts[1], 10); yy = parseInt(parts[2], 10);
              if (isNaN(dd) || isNaN(mm) || isNaN(yy)) {
                yy = parseInt(parts[0], 10); mm = parseInt(parts[1], 10); dd = parseInt(parts[2], 10);
              }
              if (!isNaN(dd) && !isNaN(mm) && !isNaN(yy)) {
                payload.reservationDate = `${yy.toString().padStart(4,'0')}-${mm.toString().padStart(2,'0')}-${dd.toString().padStart(2,'0')}`;
              } else {
                payload.reservationDate = null;
              }
            } else {
              payload.reservationDate = null;
            }
          } catch {
            payload.reservationDate = null;
          }
          payload.reservationTime = selectedTime || null;
        }
      }
      const response = await axios.post(`${API}/reservations`, payload);
      
      setReservationDetails(response.data);
      setSubmitStatus('success');
      
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', guests: '2', menuChoice: 'meat' });
        setMenuSelections([]);
        setSelectedDate('');
        setSelectedTime('');
        setReservationType('event');
        setSubmitStatus(null);
        setReservationDetails(null);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Reservation error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/notifications/subscribe`, {
        email: notifyEmail,
        eventVolume: eventInfo.currentVolume.number
      });
      
      setSubmitStatus('notified');
    } catch (error) {
      console.error('Notification error:', error);
      setSubmitStatus('notified');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Success state for reservation
  if (submitStatus === 'success') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`max-h-[90vh] max-w-lg overflow-hidden ${modalShellClassName}`}>
          <div className="max-h-[90vh] overflow-y-auto p-6 space-y-6 sm:p-8">
            <DialogHeader className="space-y-3 text-left">
              <div className="inline-flex justify-center items-center w-14 h-14 rounded-2xl border border-emerald-400/20 bg-emerald-400/10">
                <PartyPopper size={28} className="text-emerald-300" />
              </div>
              <div>
                <DialogTitle className="text-3xl font-black tracking-tight">Réservation reçue</DialogTitle>
                <DialogDescription className="mt-2 max-w-md text-sm leading-6 text-slate-400">
                  Votre demande est bien enregistrée et reste en attente de confirmation par l’équipe.
                </DialogDescription>
              </div>
            </DialogHeader>

            <SectionCard
              eyebrow="Récapitulatif"
              title={`Merci ${reservationDetails?.name || ''}`.trim()}
              description="Nous revenons vers vous par email dès que votre table est confirmée."
            >
              {reservationDetails && (
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="p-4 rounded-2xl border border-white/10 bg-black/20">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Total</p>
                    <p className="mt-2 text-2xl font-bold text-white">{reservationDetails.totalPrice}€</p>
                  </div>
                  <div className="p-4 rounded-2xl border border-white/10 bg-black/20">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Personnes</p>
                    <p className="mt-2 text-2xl font-bold text-white">{reservationDetails.guests}</p>
                  </div>
                  <div className="p-4 rounded-2xl border border-white/10 bg-black/20">
                    <p className="text-xs uppercase tracking-[0.22em] text-slate-500">Édition</p>
                    <p className="mt-2 text-2xl font-bold text-white">Vol.{reservationDetails.eventVolume}</p>
                  </div>
                </div>
              )}
            </SectionCard>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-500">Cette fenêtre se ferme automatiquement dans quelques secondes.</p>
              <Button onClick={onClose} className="px-5 h-11 text-black bg-white rounded-xl hover:bg-slate-200">
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Success state for notification signup
  if (submitStatus === 'notified') {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`max-h-[90vh] max-w-lg overflow-hidden ${modalShellClassName}`}>
          <div className="max-h-[90vh] overflow-y-auto p-6 space-y-6 sm:p-8">
            <DialogHeader className="space-y-3 text-left">
              <div className="inline-flex justify-center items-center w-14 h-14 rounded-2xl border border-emerald-400/20 bg-emerald-400/10">
                <Bell size={28} className="text-emerald-300" />
              </div>
              <div>
                <DialogTitle className="text-3xl font-black tracking-tight">C&apos;est noté</DialogTitle>
                <DialogDescription className="mt-2 max-w-md text-sm leading-6 text-slate-400">
                  Vous recevrez un email dès que les réservations ouvrent pour le Vol.{eventInfo.currentVolume.number}.
                </DialogDescription>
              </div>
            </DialogHeader>

            <SectionCard
              eyebrow="Notification"
              title="Vous êtes sur la liste"
              description="Aucune action supplémentaire n’est nécessaire, on vous prévient dès l’ouverture."
            >
              <div className="flex flex-wrap gap-2">
                <InfoChip icon={Calendar} accent>{eventInfo.currentVolume.date || 'Date à venir'}</InfoChip>
                <InfoChip icon={Bell}>Email enregistré</InfoChip>
              </div>
            </SectionCard>

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setSubmitStatus(null);
                  setNotifyEmail('');
                  onClose();
                }}
                className="px-5 h-11 text-black bg-white rounded-xl hover:bg-slate-200"
              >
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Reservations CLOSED - Show notification signup
  if (!reservationsOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`max-h-[90vh] max-w-xl overflow-hidden ${modalShellClassName}`}>
          <div className="max-h-[90vh] overflow-y-auto p-5 space-y-5 sm:space-y-6 sm:p-8">
            <DialogHeader className="space-y-4 text-left">
              <div>
                <DialogTitle className="text-3xl font-black tracking-tight">Réserver</DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-6 text-slate-400">
                  Les réservations ne sont pas encore ouvertes, mais vous pouvez déjà demander une alerte.
                </DialogDescription>
              </div>
            </DialogHeader>

            <SectionCard
              eyebrow="Newsletter"
              title="Recevoir l’ouverture"
              description={`Entrez votre email et nous vous prévenons dès que le Vol.${eventInfo.currentVolume.number} ouvre à la réservation.`}
              className="p-4 sm:p-5"
            >
              {eventInfo.currentVolume.time && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <InfoChip icon={Clock3}>{eventInfo.currentVolume.time}</InfoChip>
                </div>
              )}
              <form onSubmit={handleNotifySubmit} className="space-y-3">
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    required
                    className={iconInputClassName}
                    placeholder="votre@email.com"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-11 font-semibold text-black bg-white rounded-xl hover:bg-slate-200"
                >
                  {isSubmitting ? 'Inscription...' : 'Me prévenir'}
                </Button>
              </form>
            </SectionCard>

            <SectionCard
              eyebrow="Suivre l’actualité"
              title=""
              description=""
              className="py-4 space-y-0"
            >
              <a
                href="https://instagram.com/crapule_bxl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] text-white transition hover:bg-white/[0.08]"
              >
                <Instagram size={18} />
                <span className="font-semibold">@crapule_bxl</span>
              </a>
            </SectionCard>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Reservations OPEN - Show full reservation form
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-h-[90vh] max-w-2xl overflow-hidden ${modalShellClassName}`}>
        <div className="max-h-[90vh] overflow-y-auto p-5 space-y-5 sm:space-y-6 sm:p-8">
          <DialogHeader className="space-y-4 text-left">
            <div>
              <DialogTitle className="text-3xl font-black tracking-tight">Réserver</DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-6 text-slate-400">
                Remplissez le formulaire pour envoyer votre demande de réservation.
              </DialogDescription>
            </div>
          </DialogHeader>

          <EventHero eventInfo={eventInfo} reservationsOpen={true} hasPrice={hasPrice} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <SectionCard
              eyebrow="Étape 1"
              title="Vos coordonnées"
              description="Les informations ci-dessous permettent de confirmer votre réservation et de vous recontacter."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="name" className="text-sm font-medium text-slate-300">
                    Nom complet
                  </Label>
                  <div className="relative">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={iconInputClassName}
                      placeholder="Jean Dupont"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-300">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={iconInputClassName}
                      placeholder="jean@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-slate-300">
                    Téléphone
                  </Label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className={iconInputClassName}
                      placeholder="+32 470 12 34 56"
                    />
                  </div>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              eyebrow="Étape 2"
              title="Votre réservation"
              description="Choisissez le nombre de personnes et, si disponible, le créneau qui vous convient."
            >
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-300">Nombre de personnes</Label>
                  <Select
                    value={formData.guests}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, guests: value }))}
                  >
                  <SelectTrigger className={selectTriggerClassName}>
                      <div className="flex gap-2 items-center">
                        <Users size={18} className="text-slate-500" />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent className={selectContentClassName}>
                      {[1, 2, 3, 4, 5, 6].map(num => (
                        <SelectItem key={num} value={num.toString()} className="text-white hover:bg-white/10">
                          {num} {num === 1 ? 'personne' : 'personnes'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {calendarConfig.enabled && (
                  <div className="p-4 space-y-4 rounded-2xl border border-white/10 bg-black/20">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium text-slate-300">Type de réservation</Label>
                      <p className="text-sm text-slate-500">Choisissez entre le jour de l’événement ou une réservation restaurant.</p>
                    </div>
                    <RadioGroup
                      value={reservationType}
                      onValueChange={(value) => setReservationType(value)}
                      className="grid gap-3"
                    >
                      <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/20">
                        <RadioGroupItem value="event" id="type-event" className="mt-1 text-white border-slate-500" />
                        <Label htmlFor="type-event" className="flex-1 cursor-pointer">
                          <span className="block font-semibold text-white">Événement</span>
                          <span className="block mt-1 text-sm leading-6 text-slate-400">Réserver pour le jour du Vol.</span>
                        </Label>
                      </div>
                      <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/20">
                        <RadioGroupItem value="regular" id="type-regular" className="mt-1 text-white border-slate-500" />
                        <Label htmlFor="type-regular" className="flex-1 cursor-pointer">
                          <span className="block font-semibold text-white">Restaurant</span>
                          <span className="block mt-1 text-sm leading-6 text-slate-400">Choisir une autre date et une autre heure.</span>
                        </Label>
                      </div>
                    </RadioGroup>

                    {reservationType === 'regular' && (
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-300">Date</Label>
                          {(() => {
                            const days = Object.keys(calendarSlots.days || {}).filter(dk => (calendarSlots.days?.[dk]?.slots || []).length > 0);
                            const monthKey = calendarSlots.month || '';
                            return (
                              <Select
                                value={selectedDate || ''}
                                onValueChange={(v) => { setSelectedDate(v); setSelectedTime(''); }}
                              >
                                <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Choisir une date" /></SelectTrigger>
                                <SelectContent className={selectContentClassName}>
                                  {days.map((dk) => {
                                    const label = `${dk}/${(monthKey.split('-')[1] || '').padStart(2, '0')}`;
                                    const value = `${monthKey}-${dk}`;
                                    return <SelectItem key={dk} value={value} className="text-white">{label}</SelectItem>;
                                  })}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium text-slate-300">Heure</Label>
                          {(() => {
                            try {
                              const dk = (selectedDate || '').split('-')[2];
                              const list = dk ? (calendarSlots.days?.[dk]?.slots || []) : [];
                              if (list.length) {
                                return (
                                  <Select value={selectedTime || ''} onValueChange={(v) => setSelectedTime(v)}>
                                    <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Choisir une heure" /></SelectTrigger>
                                    <SelectContent className={selectContentClassName}>
                                      {list.map((s, i) => <SelectItem key={i} value={s} className="text-white">{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                            } catch {}
                            return (
                              <Input
                                type="time"
                                min="00:00"
                                max="23:59"
                                value={selectedTime}
                                onChange={(e) => setSelectedTime(e.target.value)}
                                className={plainInputClassName}
                              />
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {reservationType === 'event' && calendarConfig.eventEnabled && !!eventInfo.currentVolume.timeSlotsEnabled && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-slate-300">Heure le jour de l’événement</Label>
                        {(() => {
                          try {
                            const parts = String(eventInfo.currentVolume.date || '').split(/[-/]/);
                            let yy = 0, mm = 0, dd = 0;
                            if (parts.length === 3) {
                              dd = parseInt(parts[0], 10); mm = parseInt(parts[1], 10); yy = parseInt(parts[2], 10);
                              if (isNaN(dd) || isNaN(mm) || isNaN(yy)) {
                                yy = parseInt(parts[0], 10); mm = parseInt(parts[1], 10); dd = parseInt(parts[2], 10);
                              }
                            }
                            const monthKey = `${yy.toString().padStart(4, '0')}-${mm.toString().padStart(2, '0')}`;
                            if (calendarSlots.month === monthKey) {
                              const dk = String(dd).padStart(2, '0');
                              const list = (calendarSlots.days?.[dk]?.slots || []);
                              if (list.length) {
                                return (
                                  <Select value={selectedTime || ''} onValueChange={(v) => setSelectedTime(v)}>
                                    <SelectTrigger className={selectTriggerClassName}><SelectValue placeholder="Choisir une heure" /></SelectTrigger>
                                    <SelectContent className={selectContentClassName}>
                                      {list.map((s, i) => <SelectItem key={i} value={s} className="text-white">{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                );
                              }
                            }
                          } catch {}
                          return (
                            <Input
                              type="time"
                              min="00:00"
                              max="23:59"
                              value={selectedTime}
                              onChange={(e) => setSelectedTime(e.target.value)}
                              className={plainInputClassName}
                            />
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </SectionCard>

            {menuOptions.length > 0 && (
              <SectionCard
                eyebrow="Étape 3"
                title="Précommande du menu"
                description="Optionnelle, elle permet de préparer le service et d’ajuster votre total avant confirmation."
              >
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="inline-flex gap-2 items-center text-sm text-slate-400">
                      <Leaf size={16} className="text-emerald-300" />
                      Ajustez les quantités pour chaque plat souhaité
                    </div>
                    <button
                      type="button"
                      onClick={() => setMenuOpen(o => !o)}
                      className="inline-flex h-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm font-medium text-slate-200 transition hover:bg-white/[0.08]"
                    >
                      {menuOpen ? 'Masquer la précommande' : 'Ouvrir la précommande'}
                    </button>
                  </div>

                  <div className={menuOpen ? 'grid gap-3' : 'hidden'}>
                    {menuOptions.map((dish, idx) => {
                      const sel = menuSelections.find(s => s.name === dish.name);
                      const qty = sel ? sel.quantity : 0;
                      return (
                        <div key={idx} className="flex flex-col gap-4 p-4 rounded-2xl border border-white/10 bg-black/20 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-white truncate">{dish.name}</div>
                            <div className="mt-1 text-xs text-slate-500">
                              {typeLabel(dish.type)}
                              {dish.vegetarian ? ' • Végétarien' : ''}
                              {dish.price != null ? ` • ${dish.price}€` : ''}
                            </div>
                          </div>
                          <div className="flex gap-3 items-center sm:w-auto">
                            <Label htmlFor={`qty-${idx}`} className="text-sm text-slate-400">Qté</Label>
                            <Input
                              id={`qty-${idx}`}
                              type="number"
                              min="0"
                              value={qty}
                              onChange={(e) => setSelectionQty(dish.name, e.target.value)}
                              className="h-11 w-20 rounded-xl border-white/10 bg-white/[0.05] text-center text-white"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </SectionCard>
            )}

            {(hasPrice || menuOptions.length) && (
              <SectionCard
                eyebrow="Récapitulatif"
                title="Total estimé"
                description={menuOptions.length ? 'Le montant évolue selon votre sélection de plats.' : 'Montant calculé selon le nombre de personnes.'}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-4xl font-black tracking-tight text-white">
                      {menuOptions.length
                        ? (computeMenuTotal() ?? 0)
                        : parseInt(formData.guests, 10) * eventInfo.currentVolume.price}€
                    </p>
                    <p className="mt-2 text-sm text-slate-500">
                      {menuOptions.length
                        ? 'Total basé sur votre sélection'
                        : `${formData.guests} x ${eventInfo.currentVolume.price}€ par personne`}
                    </p>
                  </div>
                  <div className="px-4 py-3 text-sm leading-6 text-amber-100 rounded-2xl border border-amber-400/20 bg-amber-400/10">
                    Confirmation finale envoyée par email après validation.
                  </div>
                </div>
              </SectionCard>
            )}

            {submitStatus === 'error' && (
              <div className="flex gap-2 items-center px-4 py-3 text-sm text-red-200 rounded-2xl border border-red-500/20 bg-red-500/10">
                <AlertCircle size={16} />
                <span>Une erreur est survenue. Veuillez réessayer.</span>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-6 text-slate-500">
                En validant, vous envoyez une demande de réservation. La confirmation finale est assurée par l’équipe.
              </p>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-5 w-full h-12 font-semibold text-black bg-white rounded-xl hover:bg-slate-200 sm:w-auto"
              >
                <span>{isSubmitting ? 'Envoi en cours...' : 'Confirmer la réservation'}</span>
                {!isSubmitting && <ArrowRight size={16} className="ml-2" />}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Reservation;
